import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { RdsDialogConfig, RdsDialogService } from '@rds/angular-components';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
    SessionService,
    SPDG_CONFIG,
    SpdgConfig,
    SpdgSsoInit,
    SpdgUserService,
    SpdgUtilityService,
} from '@spdg/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { firstValueFrom, from, skip } from 'rxjs';

import { appResponseTap } from './app-response-tap';
import { NoAccessDialogComponent } from './page/start/no-access-dialog/no-access-dialog.component';
import { SessionExpiredDialogComponent } from './page/start/session-expired-dialog/session-expired-dialog.component';
import { FaviconService } from './service/favicon.service';
import { NotifyService } from './service/notify.service';
import { UserService } from './service/user.service';
import { TermsOfServiceConfiguration } from './terms-of-service.config';
import { authCodeFlowConfig } from './utility/auth-flow';
import { routes } from './utility/constant/config';

@UntilDestroy()
@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false,
})
export class AppComponent implements OnInit {
    public appEnabled = false;
    public title = 'sdt-frontend';
    isAccessDenied = false;

    constructor(
        private readonly oAuthService: OAuthService,
        public router: Router,
        public route: ActivatedRoute,
        private readonly userService: SpdgUserService,
        private readonly dialogService: RdsDialogService,
        @Inject(SPDG_CONFIG) private readonly spdgConfig: SpdgConfig,
        private readonly userSDHService: UserService,
        private readonly notifyService: NotifyService,
        private readonly utilityService: SpdgUtilityService,
        private readonly us: UserService,
        private readonly sessionService: SessionService,
        private readonly cdr: ChangeDetectorRef,
        private readonly faviconService: FaviconService
    ) {}

    public ngOnInit() {
        this.faviconService.registerResetOnLeave();
        this.faviconService.watchRouterAndApply();
        this.userService.startCrossTabLogoutListener();
        this.listenForSessionExpiry();
        void this.initApp();
    }

    private listenForSessionExpiry(): void {
        this.sessionService.expired$
            .pipe(untilDestroyed(this))
            .subscribe(() => {
                console.warn('[SDH] Session expired');

                const dialogRef = this.dialogService.open(
                    SessionExpiredDialogComponent,
                    {
                        ariaLabel: 'Session Expired',
                        closeOnNavigation: false,
                        disableClose: true,
                    }
                );

                dialogRef.afterClosed().subscribe(() => {
                    this.userService.logout();
                });
            });
    }

    private async handleLoginCommand(initiator: SpdgSsoInit): Promise<void> {
        if (this.oAuthService.hasValidAccessToken()) {
            await initiator.reengageSession();
            await initiator.redirectIfNeeded(undefined, true);
        } else {
            await initiator.login();
        }
    }

    private async initApp() {
        try {
            this.userService.parseSuiteConfiguration();
        } catch (ex) {
            this.router.navigate(['/configuration-failed']);
            console.error(ex);
        }

        this.oAuthService.configure(authCodeFlowConfig);

        const paramMap = await firstValueFrom(this.route.queryParamMap);

        const urlTree = this.router.parseUrl(globalThis.location.search);
        const redirectToParam = urlTree.queryParamMap.get('redirectTo');

        console.log('Detected redirectTo:', redirectToParam); // Debugging

        const storedRedirect = sessionStorage.getItem('postLoginRedirectUrl');
        const postLoginRedirectUrl: string =
            redirectToParam ?? storedRedirect ?? routes.projectURL;

        if (redirectToParam) {
            sessionStorage.setItem('postLoginRedirectUrl', redirectToParam);
        }

        const initiator = new SpdgSsoInit({
            appEnable: (status: boolean) =>
                status ? this.onAppEnabled() : this.onAppDisabled(),
            appLoginError: (error) => {},
            appNoAccess: () => {
                this.dialogService.open(NoAccessDialogComponent, {
                    ariaLabel: 'No access',
                    closeOnNavigation: false,
                    disableClose: true,
                    width: '600px',
                } as Partial<RdsDialogConfig<unknown>>);
                sessionStorage.clear();
            },
            onSessionExpired: () => {},

            onSessionEstablished: () => {
                console.debug(
                    '[SDH] Core session established → start monitoring'
                );
                this.sessionService.startMonitoring();
            },
            config: this.spdgConfig,
            instanceOAuth: this.oAuthService,
            instanceRouter: this.router,
            instanceUserService: this.userService,
            pathLoginPage: '/login',
            pathStartPage: postLoginRedirectUrl || routes.projectURL,
            routerParamMap: paramMap,
            termsOfServiceConfiguration: TermsOfServiceConfiguration,
            appShowTermsDialog: (deltaObject) => {
                console.log('Bypassing ToC and forcing initialization...');

                SpdgSsoInit.registerAgreeToTerms(deltaObject.delta);
                this.appEnabled = true;

                if (this.oAuthService.hasValidAccessToken()) {
                    // Change .subscribe() to .then()
                    this.userSDHService
                        .establishSession()
                        .then(() => {
                            this.establishSession();

                            const finalTarget =
                                sessionStorage.getItem(
                                    'postLoginRedirectUrl'
                                ) || routes.projectURL;
                            this.router.navigateByUrl(finalTarget);
                        })
                        .catch((err) => {
                            console.error(
                                'Failed to establish session during bypass',
                                err
                            );
                            this.appEnabled = true; // Still enable so the user isn't stuck on a blank screen
                        });
                }
            },
        });

        this.sessionService.setOnRefreshSuccess(async () => {
            console.debug('[SDH] Token refreshed → reengaging session');
            await initiator.reengageSession();
        });

        this.utilityService.setTap((res: any) =>
            appResponseTap(res, {
                navigateNotFound: () => this.router.navigate(['not-found']),
                notifyError: (message) =>
                    this.notifyService.notification('error', message),
                openNoAccessDialog: (data) => {
                    this.isAccessDenied = true;
                    this.dialogService
                        .open(NoAccessDialogComponent, { data })
                        .afterClosed()
                        .subscribe((hasConfirmed: boolean) => {
                            if (hasConfirmed) {
                                this.isAccessDenied = false;
                            }
                        });
                },
            })
        );

        this.userService.loginCommand
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                if (!value) {
                    return;
                }
                void this.handleLoginCommand(initiator);
            });

        await initiator.initApp();

        this.loadApp();
    }

    private loadApp() {
        this.userService.sessionExpiredCommand.pipe(skip(1)).subscribe(() => {
            const dialogRef = this.dialogService.open(
                SessionExpiredDialogComponent,
                {
                    ariaLabel: 'Session Expired',
                    closeOnNavigation: false,
                    disableClose: true,
                }
            );
            dialogRef.afterClosed().subscribe((result) => {
                if (result === true) {
                    this.userService.logout(false);
                }
            });
        });
        this.userService.identityEstablished
            .pipe(untilDestroyed(this))
            .subscribe({
                next: (status) => {
                    if (!this.appEnabled && status) {
                        this.appEnabled = true;
                        if (this.oAuthService.hasValidAccessToken()) {
                            this.userSDHService.establishSession();
                        }
                    }
                },
            });
    }

    /** Handle SSO reporting the app as enabled — establish the service session if a valid token exists. */
    private onAppEnabled(): void {
        if (this.oAuthService.hasValidAccessToken()) {
            from(this.userSDHService.establishSession()).subscribe({
                next: () => {
                    this.establishSession();
                },
                error: (err) => {
                    console.error('[SDH] Service session failed', err);
                    this.appEnabled = true;
                },
            });
        } else {
            this.appEnabled = true;
        }
    }

    /** Handle SSO reporting the app as disabled. */
    private onAppDisabled(): void {
        this.appEnabled = false;
    }

    public establishSession() {
        this.us.saveUser().subscribe({
            next: () => {
                this.appEnabled = true;
                this.us.fetchUserRolePermissions().subscribe();
            },
            error: () => (this.appEnabled = true),
        });
    }
}
